import { useMemo } from 'react';
import type { Euler } from 'three';

import { getKeyPoseFkJointWorldTransform } from '../fk/keyPoseFkSystem';
import { createEditableKeyPoseScene } from '../ik/keyPoseIkSystem';
import { useStore as useKeyPoseStore } from '../keyPoseStore';
import { useCurrentFrameKeyPoseEditing } from './useCurrentFrameKeyPoseEditing';

interface UseKeyPoseFkEditingInput {
  sceneRotation?: Euler;
}

export function useKeyPoseFkEditing({
  sceneRotation,
}: UseKeyPoseFkEditingInput) {
  const keyPoseStore = useKeyPoseStore();
  const context = useCurrentFrameKeyPoseEditing({ sceneRotation });
  const { actorScene, keyPose } = context;
  const { editing } = keyPoseStore.state;
  const jointId = editing.mode === 'fk' ? editing.selectedFkJointId : null;
  const handleTransform = useMemo(() => {
    if (!keyPose || !jointId) {
      return null;
    }

    const scene = createEditableKeyPoseScene({
      actorScene,
      keyPose,
      sceneRotation,
    });

    return scene ? getKeyPoseFkJointWorldTransform({ scene, jointId }) : null;
  }, [actorScene, jointId, keyPose, sceneRotation]);

  return {
    ...context,
    handleTransform,
    lockFeet: editing.lockFeet,
    jointId,
  };
}
